/**
 * /assets/custom-theme.js
 * The Social Game Shopify Theme Logic
 */

document.addEventListener('DOMContentLoaded', () => {
  // Bind core modules
  initPlexusCanvas();
  initMouseGlowFollower();
  initStatsCounters();
  initFaqAccordion();
  initCartDrawer();
  initQuickViewModal();
  initSpotlightSlider();
  initHeaderScroll();
});

// --- 1. Interactive Canvas Plexus Background ---
function initPlexusCanvas() {
  const canvas = document.getElementById('hero-plexus-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  let animationFrameId;
  let isCanvasVisible = true;

  let width = canvas.width = window.innerWidth;
  let height = canvas.height = window.innerHeight;

  const particles = [];
  const isMobile = window.innerWidth < 768;
  const maxParticles = isMobile ? 18 : 55;
  const connectionDist = 120;
  const speedScale = isMobile ? 0.3 : 0.45;
  const drawLines = !isMobile; // Completely disable O(N^2) line drawing on mobile to save CPU/battery

  class Particle {
    constructor() {
      this.x = Math.random() * width;
      this.y = Math.random() * height;
      this.vx = (Math.random() - 0.5) * speedScale;
      this.vy = (Math.random() - 0.5) * speedScale;
      this.r = Math.random() * 2 + 1;
    }
    update() {
      this.x += this.vx;
      this.y += this.vy;

      if (this.x < 0 || this.x > width) this.vx *= -1;
      if (this.y < 0 || this.y > height) this.vy *= -1;
    }
    draw() {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 140, 0, 0.45)';
      ctx.fill();
    }
  }

  for (let i = 0; i < maxParticles; i++) {
    particles.push(new Particle());
  }

  function animate() {
    // Pause animation completely if tab is hidden or canvas is scrolled off-screen
    if (document.hidden || !isCanvasVisible) {
      animationFrameId = requestAnimationFrame(animate);
      return;
    }

    ctx.clearRect(0, 0, width, height);

    for (let i = 0; i < particles.length; i++) {
      particles[i].update();
      particles[i].draw();

      if (drawLines) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDist) {
            const alpha = (1 - dist / connectionDist) * 0.12;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255, 140, 0, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
    }
    animationFrameId = requestAnimationFrame(animate);
  }

  // IntersectionObserver to pause loop when off-screen
  if (window.IntersectionObserver) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isCanvasVisible = entry.isIntersecting;
      });
    }, { threshold: 0.01 });
    observer.observe(canvas);
  }

  animate();

  // Debounced window resize event handler
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    }, 150);
  }, { passive: true });
}

// --- 2. Interactive Mouse-Glow Follower ---
function initMouseGlowFollower() {
  const heroSection = document.getElementById('hero-section');
  const mouseGlow = document.getElementById('hero-mouse-glow');

  if (heroSection && mouseGlow && window.gsap) {
    heroSection.addEventListener('mousemove', (e) => {
      const rect = heroSection.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      // Smoothly position mouse-glow orb using GSAP
      gsap.to(mouseGlow, { duration: 0.6, left: mx, top: my, opacity: 1, ease: 'power2.out' });
    });

    heroSection.addEventListener('mouseleave', () => {
      gsap.to(mouseGlow, { duration: 0.5, opacity: 0 });
    });
  }
}

// --- 3. Dynamic Stats Counter ---
function initStatsCounters() {
  const stats = document.querySelectorAll('.stat-num');
  if (stats.length === 0) return;

  const animateStats = () => {
    stats.forEach(stat => {
      const target = parseFloat(stat.getAttribute('data-target'));
      const duration = 2000;
      const startTime = performance.now();

      const updateCount = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = progress * (2 - progress);
        const currentVal = easeProgress * target;

        if (target % 1 === 0) {
          stat.textContent = Math.floor(currentVal).toLocaleString() + (target >= 10000 ? '+' : '%');
        } else {
          stat.textContent = currentVal.toFixed(1) + '/5';
        }

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        } else {
          if (target % 1 === 0) {
            stat.textContent = target.toLocaleString() + (target >= 10000 ? '+' : '%');
          } else {
            stat.textContent = target.toFixed(1) + '/5';
          }
        }
      };

      requestAnimationFrame(updateCount);
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        animateStats();
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  const statsRow = document.querySelector('.hero-stats-row');
  if (statsRow) observer.observe(statsRow);
}

// --- 4. FAQ Accordion Toggle ---
function initFaqAccordion() {
  const faqItems = document.querySelectorAll('.faq-item');
  faqItems.forEach(item => {
    const question = item.querySelector('.faq-question, .faq-trigger');
    const answer = item.querySelector('.faq-answer');
    const icon = item.querySelector('.faq-icon');

    if (question && answer) {
      question.addEventListener('click', () => {
        const isExpanded = question.getAttribute('aria-expanded') === 'true';
        
        faqItems.forEach(otherItem => {
          if (otherItem !== item) {
            const otherQ = otherItem.querySelector('.faq-question, .faq-trigger');
            const otherA = otherItem.querySelector('.faq-answer');
            const otherI = otherItem.querySelector('.faq-icon');
            if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
            if (otherA) otherA.setAttribute('hidden', '');
            if (otherI) otherI.textContent = '+';
          }
        });

        question.setAttribute('aria-expanded', !isExpanded);
        if (isExpanded) {
          answer.setAttribute('hidden', '');
          if (icon) icon.textContent = '+';
        } else {
          answer.removeAttribute('hidden');
          if (icon) icon.textContent = '-';
        }
      });
    }
  });
}

// --- 5. Quick View Product Modal Fetcher ---
function initQuickViewModal() {
  const modal = document.getElementById('product-modal');
  const closeBtn = document.getElementById('modal-close-btn');

  if (!modal) return;

  if (closeBtn) {
    closeBtn.addEventListener('click', () => closeQuickViewModal());
  }

  document.addEventListener('click', (e) => {
    const qvBtn = e.target.closest('.card-quick-btn, .spotlight-quick-btn');
    if (qvBtn) {
      const handle = qvBtn.getAttribute('data-handle');
      if (handle) {
        fetchProductJSONAndOpenModal(handle);
      }
    }
  });
}

function fetchProductJSONAndOpenModal(handle) {
  const modal = document.getElementById('product-modal');
  if (!modal) return;

  fetch(`/products/${handle}.js`)
    .then(res => res.json())
    .then(product => {
      document.getElementById('modal-product-title').textContent = product.title;
      document.getElementById('modal-product-category').textContent = product.type || 'DIGITAL';
      document.getElementById('modal-product-description').innerHTML = product.description;
      
      const formattedPrice = Shopify.formatMoney ? Shopify.formatMoney(product.price) : `₹${(product.price / 100).toFixed(2)}`;
      document.getElementById('modal-price-amount').textContent = formattedPrice.replace(/[^\d\.]/g, '');
      document.getElementById('modal-price-symbol').textContent = formattedPrice.replace(/[\d\.\s]/g, '');

      const track = document.getElementById('modal-slider-track');
      const dots = document.getElementById('modal-slider-dots');
      track.innerHTML = '';
      dots.innerHTML = '';

      const images = product.images.length > 0 ? product.images : ['horror_bundle.jpg'];
      images.forEach((imgUrl, index) => {
        const img = document.createElement('img');
        img.src = imgUrl;
        img.alt = product.title;
        img.style.objectFit = 'contain';
        img.style.background = '#000000';
        track.appendChild(img);

        const dot = document.createElement('span');
        dot.className = index === 0 ? 'dot active' : 'dot';
        dot.setAttribute('data-index', index);
        dot.addEventListener('click', () => {
          scrollModalToSlide(index);
        });
        dots.appendChild(dot);
      });

      const addBtn = document.getElementById('modal-add-to-cart-btn');
      const buyBtn = document.getElementById('modal-buy-button');
      
      if (addBtn) {
        const newAddBtn = addBtn.cloneNode(true);
        addBtn.parentNode.replaceChild(newAddBtn, addBtn);
        newAddBtn.addEventListener('click', () => {
          addToCartAJAX(product.variants[0].id, newAddBtn);
        });
      }

      if (buyBtn) {
        buyBtn.href = `/cart/${product.variants[0].id}:1`;
      }

      const featuresContainer = document.getElementById('modal-product-features');
      featuresContainer.innerHTML = '';
      
      const features = [
        "100% Fully Editable & Customizable",
        "Includes Video Tutorial Guidelines",
        "Format: Optimized High-Resolution Files",
        "Instant Email Delivery & Lifetime Updates"
      ];
      
      features.forEach(feat => {
        const li = document.createElement('li');
        li.innerHTML = `<span class="check-icon">✓</span> ${feat}`;
        featuresContainer.appendChild(li);
      });

      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    })
    .catch(err => {
      console.error('Failed to fetch product data:', err);
    });
}

function scrollModalToSlide(index) {
  const track = document.getElementById('modal-slider-track');
  const dots = document.getElementById('modal-slider-dots');
  if (track) {
    track.style.transform = `translateX(-${index * 100}%)`;
  }
  if (dots) {
    dots.querySelectorAll('.dot').forEach((dot, idx) => {
      if (idx === index) dot.classList.add('active');
      else dot.classList.remove('active');
    });
  }
}

function addToCartAJAX(variantId, btn) {
  animateBtnLoading(btn);
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(res => res.json())
  .then(() => {
    animateBtnSuccess(btn);
    closeQuickViewModal();
    if (window.refreshCartUIAndOpen) {
      window.refreshCartUIAndOpen();
    }
  })
  .catch(err => {
    console.error('Modal add to cart error:', err);
    resetBtnState(btn);
  });
}

function closeQuickViewModal() {
  const modal = document.getElementById('product-modal');
  if (modal) {
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

// --- 6. Shopify AJAX Cart Drawer ---
function initCartDrawer() {
  // Bind default forms that submit to cart to trigger AJAX
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[action="/cart/add"]');
    // If form has a return_to field (Buy Now button), don't intercept it
    if (form && !form.querySelector('input[name="return_to"]')) {
      e.preventDefault();
      
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) animateBtnLoading(submitBtn);

      const formData = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(res => res.json())
      .then((data) => {
        if (submitBtn) animateBtnSuccess(submitBtn);
        // Show Toast confirmation feedback
        showToast('🛒 Item added to cart successfully!', 'Checkout', '/cart');

        // Fetch updated cart to get new item count
        fetch('/cart.js')
          .then(res => res.json())
          .then(cartData => {
            const badge = document.getElementById('cart-badge-count');
            if (badge) badge.textContent = cartData.item_count;
          });
        
        // Dynamic drawer display for Sense theme
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.classList.remove('is-empty');
          // Dispatch native cart update event to let Sense fetch new lines
          const cartEvent = new Event('cart:updated', { bubbles: true });
          drawer.dispatchEvent(cartEvent);
          if (typeof drawer.open === 'function') {
            drawer.open();
          } else {
            drawer.classList.add('active');
          }
        }
      })
      .catch(err => {
        console.error('Cart add error:', err);
        if (submitBtn) resetBtnState(submitBtn);
        showToast('❌ Failed to add product. Please try again.', 'Retry', '#');
      });
    }
  });
}

// --- Spotlight Horizontal Slider Arrow Listeners ---
function initSpotlightSlider() {
  const track = document.getElementById('spotlight-slider-track');
  const prev = document.getElementById('spotlight-prev');
  const next = document.getElementById('spotlight-next');
  
  if (track && prev && next) {
    let scrollAmount = 0;
    const cardWidth = 336;

    next.addEventListener('click', () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      scrollAmount = Math.min(scrollAmount + cardWidth, maxScroll);
      track.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    });

    prev.addEventListener('click', () => {
      scrollAmount = Math.max(scrollAmount - cardWidth, 0);
      track.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    });
  }
}

// --- Button States Animations ---
function animateBtnLoading(btn) {
  btn.classList.add('loading');
  btn.setAttribute('disabled', 'true');
  btn.dataset.originalHtml = btn.innerHTML;
  btn.innerHTML = `Loading...`;
}

function animateBtnSuccess(btn) {
  btn.classList.remove('loading');
  btn.classList.add('added');
  btn.innerHTML = `Added`;
  setTimeout(() => {
    resetBtnState(btn);
  }, 1500);
}

function resetBtnState(btn) {
  btn.classList.remove('added', 'loading');
  btn.removeAttribute('disabled');
  if (btn.dataset.originalHtml) {
    btn.innerHTML = btn.dataset.originalHtml;
  }
}

// --- Dynamic Toast Popup Notification Feedback System ---
function showToast(message, actionText, actionLink) {
  let container = document.getElementById('toast-notification-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-notification-container';
    container.style.cssText = `
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 999999;
      display: flex;
      flex-direction: column-reverse;
      gap: 12px;
      pointer-events: none;
    `;
    document.body.appendChild(container);
  }
  
  const toast = document.createElement('div');
  toast.className = 'custom-toast-msg';
  toast.style.cssText = `
    background: rgba(14, 14, 21, 0.95);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 140, 0, 0.3);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 140, 0, 0.1);
    color: #FFFFFF;
    padding: 16px 20px;
    border-radius: 12px;
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 320px;
    pointer-events: auto;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  `;
  
  const textNode = document.createElement('span');
  textNode.innerHTML = message;
  
  const btnNode = document.createElement('a');
  btnNode.href = actionLink || '/cart';
  btnNode.textContent = actionText || 'Checkout';
  btnNode.style.cssText = `
    background: var(--accent-orange, #FF8C00);
    color: #040407;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
    transition: transform 0.2s ease;
  `;
  btnNode.addEventListener('mouseenter', () => btnNode.style.transform = 'scale(1.05)');
  btnNode.addEventListener('mouseleave', () => btnNode.style.transform = 'scale(1)');
  
  toast.appendChild(textNode);
  toast.appendChild(btnNode);
  container.appendChild(toast);
  
  // Trigger entrance transition
  setTimeout(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  }, 10);
  
  // Automatically remove after 4.5 seconds
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(20px)';
    setTimeout(() => {
      toast.remove();
    }, 300);
  }, 4500);
}

// Fallback Add to Cart handler for mockup products
window.handleAddToCart = function(btn, slugId) {
  animateBtnLoading(btn);
  setTimeout(() => {
    animateBtnSuccess(btn);
    showToast(`💡 Simulated: "<b>${slugId}</b>" added to cart! Connect a collection in the customizer to go live.`, 'Customize', '/admin');
  }, 600);
};

// Scroll Handler for Transparent-to-Glass Header Transition
function initHeaderScroll() {
  const header = document.getElementById('main-header');
  if (!header) return;

  function handleScroll() {
    if (window.scrollY > 10) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }

  handleScroll();
  window.addEventListener('scroll', handleScroll, { passive: true });
}


// Force sync trigger - bottom position


